import type { FormDataOf } from '../../../../types/templates';
import { macroSacaTuTarjeta } from '..';
import { getStageInfo, getChannelsWithVariant, isValidCombination, getAvailableVariants } from './validation-helpers';

type FormData = FormDataOf<typeof macroSacaTuTarjeta>;

// Reactive field logic. 3 cases — user changed STAGE, CHANNEL, or VARIANT.
export const applyFieldLogic = (fieldKey: string, value: unknown, formData: FormData): Partial<FormData> => {
  const updates: Partial<FormData> = {};

  if (fieldKey === 'stage') {
    const newStage = value as string;
    const currentChannel = formData.adChannel;
    const currentVariant = formData.variant;
    const stageInfo = getStageInfo(newStage);

    // Auto-pick channel if only one available and none selected.
    if (stageInfo.channels.length === 1 && !currentChannel) {
      updates.adChannel = stageInfo.channels[0];
    }

    // Replace stale channel with the first valid for the new stage.
    if (currentChannel && !stageInfo.channels.includes(currentChannel as 'google' | 'meta')) {
      updates.adChannel = stageInfo.channels[0];
    }

    const finalChannel = updates.adChannel || currentChannel;

    if (finalChannel) {
      if (!isValidCombination(newStage, finalChannel, currentVariant ?? '')) {
        updates.variant = getAvailableVariants(newStage, finalChannel)[0];
      }
    } else if (!stageInfo.allVariants.includes(currentVariant ?? '')) {
      // Defensive: when multiple channels exist and none is picked, ensure
      // the current variant still exists somewhere in the new stage.
      updates.variant = getAvailableVariants(newStage, stageInfo.channels[0])[0];
    }
  }

  if (fieldKey === 'adChannel') {
    const newChannel = value as string;
    const currentStage = formData.stage;
    const currentVariant = formData.variant;

    if (!isValidCombination(currentStage ?? '', newChannel, currentVariant ?? '')) {
      updates.variant = getAvailableVariants(currentStage ?? '', newChannel)[0];
    }
  }

  if (fieldKey === 'variant') {
    const newVariant = value as string;
    const currentStage = formData.stage;
    const currentChannel = formData.adChannel;

    if (!currentStage) return updates;

    const channelsWithVariant = getChannelsWithVariant(currentStage, newVariant);

    if (channelsWithVariant.length === 1) {
      updates.adChannel = channelsWithVariant[0];
    } else if (currentChannel && !channelsWithVariant.includes(currentChannel)) {
      updates.adChannel = channelsWithVariant[0];
    }
  }

  return updates;
};
